import { supabase } from '../lib/supabaseClient';
import OfflineCalendarStorage from './offlineCalendarStorage';
import type { OfflineAppointment } from './offlineCalendarStorage';

const offlineStorage = OfflineCalendarStorage.getInstance();

// Helper to check if we're online
const isOnline = (): boolean => {
  return navigator.onLine;
};

// Helper to generate temporary offline IDs
const generateOfflineId = (): string => {
  return `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export type AppointmentStatus = 'to_be_completed' | 'attended' | 'cancelled';

export interface Appointment {
  id: string;
  user_id: string;
  organization_id?: string;
  assigned_to_user_id?: string;
  title?: string;
  appointment_type: string;
  date: string;
  time: string;
  address?: string;
  client_name?: string;
  phone_number?: string;
  notes?: string;
  status: AppointmentStatus;
  photo_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AppointmentType {
  id: string;
  user_id: string;
  organization_id?: string;
  type_name: string;
  is_default: boolean;
  created_at?: string;
}

export interface AppointmentNote {
  id: string;
  appointment_id: string;
  user_id: string;
  note_text: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Fetch all appointments for the current user or a specific user (for admins viewing team calendars)
 */
export async function fetchAppointments(viewUserId?: string): Promise<Appointment[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  try {
    if (isOnline()) {
      // Try to fetch from Supabase
      let query = supabase
        .from('appointments')
        .select('*')
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (viewUserId) {
        query = query.or(`user_id.eq.${viewUserId},assigned_to_user_id.eq.${viewUserId}`);
      } else {
        query = query.or(`and(user_id.eq.${user.id},assigned_to_user_id.is.null),assigned_to_user_id.eq.${user.id}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Save to offline storage for caching
      if (data) {
        for (const appointment of data) {
          await offlineStorage.saveAppointment(appointment, false);
        }
      }
      
      return data || [];
    } else {
      // Offline mode - fetch from IndexedDB
      console.log('[AppointmentService] Offline mode - fetching from local storage');
      return await offlineStorage.getAppointments(viewUserId || user.id);
    }
  } catch (error) {
    console.warn('[AppointmentService] Online fetch failed, falling back to offline storage:', error);
    // Fallback to offline storage
    return await offlineStorage.getAppointments(viewUserId || user.id);
  }
}

/**
 * Fetch appointments for a specific date
 */
export async function fetchAppointmentsByDate(date: string): Promise<Appointment[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  try {
    if (isOnline()) {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('date', date)
        .order('time', { ascending: true });

      if (error) throw error;
      
      // Cache in offline storage
      if (data) {
        for (const appointment of data) {
          await offlineStorage.saveAppointment(appointment, false);
        }
      }
      
      return data || [];
    } else {
      return await offlineStorage.getAppointmentsByDate(date);
    }
  } catch (error) {
    console.warn('[AppointmentService] Falling back to offline storage:', error);
    return await offlineStorage.getAppointmentsByDate(date);
  }
}

/**
 * Create a new appointment
 */
export async function createAppointment(appointment: Omit<Appointment, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<Appointment> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  if (isOnline()) {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .insert([{
          ...appointment,
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Save to offline storage as synced
      await offlineStorage.saveAppointment(data, false);
      
      return data;
    } catch (error) {
      console.warn('[AppointmentService] Online create failed, saving offline:', error);
      // Fall through to offline creation
    }
  }
  
  // Offline mode - save locally with pending sync
  console.log('[AppointmentService] Creating appointment offline');
  const offlineAppointment: Appointment = {
    ...appointment,
    id: generateOfflineId(),
    user_id: user.id,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  await offlineStorage.saveAppointment(offlineAppointment, true);
  return offlineAppointment;
}

/**
 * Update an existing appointment
 */
export async function updateAppointment(id: string, updates: Partial<Appointment>): Promise<Appointment> {
  if (isOnline() && !id.startsWith('offline-')) {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      // Update offline storage
      await offlineStorage.saveAppointment(data, false);
      
      return data;
    } catch (error) {
      console.warn('[AppointmentService] Online update failed, updating offline:', error);
      // Fall through to offline update
    }
  }
  
  // Offline mode or offline ID - update locally
  console.log('[AppointmentService] Updating appointment offline');
  const appointments = await offlineStorage.getAppointments();
  const existing = appointments.find(a => a.id === id);
  
  if (!existing) {
    throw new Error('Appointment not found');
  }
  
  const updated: Appointment = {
    ...existing,
    ...updates,
    updated_at: new Date().toISOString()
  };
  
  await offlineStorage.saveAppointment(updated, true);
  return updated;
}

/**
 * Delete an appointment
 */
export async function deleteAppointment(id: string): Promise<void> {
  if (isOnline() && !id.startsWith('offline-')) {
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.warn('[AppointmentService] Online delete failed:', error);
    }
  }
  
  // Always delete from offline storage
  await offlineStorage.deleteAppointment(id);
}

/**
 * Upload a photo for an appointment
 */
export async function uploadAppointmentPhoto(file: File): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fileExt = file.name.split('.').pop();
  const fileName = `${user.id}/${Date.now()}.${fileExt}`;

  if (isOnline()) {
    try {
      const { error: uploadError } = await supabase.storage
        .from('calendar-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      return fileName; // Store the path, not the full URL
    } catch (error) {
      console.warn('[AppointmentService] Photo upload failed, saving offline:', error);
      // Fall through to offline storage
    }
  }
  
  // Offline mode - save photo to IndexedDB
  console.log('[AppointmentService] Saving photo offline');
  const photoId = generateOfflineId();
  await offlineStorage.savePhoto({
    id: photoId,
    file,
    appointmentId: '', // Will be set when associated with appointment
    _syncStatus: 'pending',
    _modifiedAt: Date.now()
  });
  
  return photoId; // Return offline ID
}

/**
 * Get public URL for an appointment photo
 */
export async function getAppointmentPhotoUrl(photoPath: string): Promise<string> {
  // Check if it's an offline photo
  if (photoPath.startsWith('offline-')) {
    const photo = await offlineStorage.getPhoto(photoPath);
    if (photo?.file) {
      // Create object URL for offline photo
      return URL.createObjectURL(photo.file);
    }
  }
  
  // Online photo - get from Supabase
  const { data: { publicUrl } } = supabase.storage
    .from('calendar-images')
    .getPublicUrl(photoPath);

  return publicUrl;
}

/**
 * Delete an appointment photo
 */
export async function deleteAppointmentPhoto(photoPath: string): Promise<void> {
  const { error } = await supabase.storage
    .from('calendar-images')
    .remove([photoPath]);

  if (error) throw error;
}

/**
 * Fetch all appointment types for the current user
 */
export async function fetchAppointmentTypes(): Promise<AppointmentType[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  try {
    if (isOnline()) {
      const { data, error } = await supabase
        .from('appointment_types')
        .select('*')
        .order('is_default', { ascending: false })
        .order('type_name', { ascending: true });

      if (error) throw error;
      
      // Cache in offline storage
      if (data) {
        for (const type of data) {
          await offlineStorage.saveAppointmentType(type, false);
        }
      }
      
      return data || [];
    } else {
      console.log('[AppointmentService] Fetching appointment types offline');
      return await offlineStorage.getAppointmentTypes();
    }
  } catch (error) {
    console.warn('[AppointmentService] Falling back to offline types:', error);
    return await offlineStorage.getAppointmentTypes();
  }
}

/**
 * Add a custom appointment type
 */
export async function addAppointmentType(typeName: string): Promise<AppointmentType> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  if (isOnline()) {
    try {
      const { data, error } = await supabase
        .from('appointment_types')
        .insert([{
          user_id: user.id,
          type_name: typeName,
          is_default: false
        }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('This appointment type already exists');
        }
        throw error;
      }
      
      await offlineStorage.saveAppointmentType(data, false);
      return data;
    } catch (error) {
      console.warn('[AppointmentService] Online type creation failed, saving offline:', error);
      // Fall through to offline
    }
  }
  
  // Offline mode
  console.log('[AppointmentService] Creating appointment type offline');
  const offlineType: AppointmentType = {
    id: generateOfflineId(),
    user_id: user.id,
    type_name: typeName,
    is_default: false,
    created_at: new Date().toISOString()
  };
  
  await offlineStorage.saveAppointmentType(offlineType, true);
  return offlineType;
}

/**
 * Delete a custom appointment type (only non-default types can be deleted)
 */
export async function deleteAppointmentType(id: string): Promise<void> {
  if (isOnline() && !id.startsWith('offline-')) {
    try {
      const { error } = await supabase
        .from('appointment_types')
        .delete()
        .eq('id', id)
        .eq('is_default', false);

      if (error) throw error;
    } catch (error) {
      console.warn('[AppointmentService] Online type delete failed:', error);
    }
  }
  
  await offlineStorage.deleteAppointmentType(id);
}

/**
 * Initialize default appointment types for a user
 */
export async function initializeDefaultAppointmentTypes(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // First, check which default types already exist
  const { data: existingTypes } = await supabase
    .from('appointment_types')
    .select('type_name')
    .eq('user_id', user.id)
    .eq('is_default', true);

  const existingTypeNames = new Set(existingTypes?.map(t => t.type_name) || []);
  const defaultTypes = ['Retrofit', 'EPC', 'FloorPlan', 'Property Inventory'];
  
  // Only insert types that don't exist yet
  const typesToInsert = defaultTypes
    .filter(typeName => !existingTypeNames.has(typeName))
    .map(typeName => ({
      user_id: user.id,
      type_name: typeName,
      is_default: true
    }));

  // Bulk insert only missing types
  if (typesToInsert.length > 0) {
    await supabase
      .from('appointment_types')
      .insert(typesToInsert);
  }
}

/**
 * Fetch notes for a specific appointment
 */
export async function fetchAppointmentNotes(appointmentId: string): Promise<AppointmentNote[]> {
  try {
    if (isOnline() && !appointmentId.startsWith('offline-')) {
      const { data, error } = await supabase
        .from('appointment_notes')
        .select('*')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Cache in offline storage
      if (data) {
        for (const note of data) {
          await offlineStorage.saveAppointmentNote(note, false);
        }
      }
      
      return data || [];
    } else {
      return await offlineStorage.getAppointmentNotes(appointmentId);
    }
  } catch (error) {
    console.warn('[AppointmentService] Falling back to offline notes:', error);
    return await offlineStorage.getAppointmentNotes(appointmentId);
  }
}

/**
 * Create a new note for an appointment
 */
export async function createAppointmentNote(
  appointmentId: string,
  noteText: string
): Promise<AppointmentNote> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  if (isOnline() && !appointmentId.startsWith('offline-')) {
    try {
      const { data, error } = await supabase
        .from('appointment_notes')
        .insert([{
          appointment_id: appointmentId,
          user_id: user.id,
          note_text: noteText
        }])
        .select()
        .single();

      if (error) throw error;
      
      await offlineStorage.saveAppointmentNote(data, false);
      return data;
    } catch (error) {
      console.warn('[AppointmentService] Online note creation failed, saving offline:', error);
      // Fall through to offline
    }
  }
  
  // Offline mode
  console.log('[AppointmentService] Creating note offline');
  const offlineNote: AppointmentNote = {
    id: generateOfflineId(),
    appointment_id: appointmentId,
    user_id: user.id,
    note_text: noteText,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  await offlineStorage.saveAppointmentNote(offlineNote, true);
  return offlineNote;
}

/**
 * Update an existing note
 */
export async function updateAppointmentNote(
  noteId: string,
  noteText: string
): Promise<AppointmentNote> {
  const { data, error } = await supabase
    .from('appointment_notes')
    .update({ note_text: noteText })
    .eq('id', noteId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete a note
 */
export async function deleteAppointmentNote(noteId: string): Promise<void> {
  if (isOnline() && !noteId.startsWith('offline-')) {
    try {
      const { error } = await supabase
        .from('appointment_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
    } catch (error) {
      console.warn('[AppointmentService] Online note delete failed:', error);
    }
  }
  
  await offlineStorage.deleteAppointmentNote(noteId);
}
